import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from '@/i18n'

export interface LoadingProps {
  /** 加载动画风格 */
  variant?: 'spinner' | 'bar'
  /** 显示的提示文案 */
  message?: string
  /** 进度百分比（0-100），不传则显示不确定进度 */
  progress?: number
  /** 延迟显示的时间，避免闪烁（毫秒） */
  delayMs?: number
  /** 大小 */
  size?: 'sm' | 'md' | 'lg'
}

/**
 * 全局 Loading 组件，支持动画样式、进度条与延迟显示
 * @param props - 组件属性
 * @returns Loading UI
 */
export const Loading = ({ variant = 'spinner', message, progress, delayMs = 150, size = 'md' }: LoadingProps) => {
  const { t } = useTranslation()
  const normalizedDelay = Math.max(0, delayMs ?? 0)
  const [show, setShow] = useState(normalizedDelay === 0)
  const defaultMessage = t('common:loading')

  useEffect(() => {
    if (normalizedDelay > 0) {
      const t = setTimeout(() => setShow(true), normalizedDelay)
      return () => clearTimeout(t)
    }
  }, [normalizedDelay])

  const sizeClasses = useMemo(() => {
    switch (size) {
      case 'sm':
        return { spinner: 'h-5 w-5', bar: 'h-1' }
      case 'lg':
        return { spinner: 'h-12 w-12', bar: 'h-2' }
      default:
        return { spinner: 'h-8 w-8', bar: 'h-1.5' }
    }
  }, [size])

  if (!show) return null

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-white/75 dark:bg-gray-900/75 backdrop-blur-sm'>
      <div className='flex flex-col items-center gap-4 px-6 py-5 rounded-lg bg-white dark:bg-gray-800 shadow-md w-[min(90vw,420px)]'>
        {variant === 'spinner' ? (
          <svg className={`${sizeClasses.spinner} animate-spin text-blue-600`} viewBox='0 0 24 24' role='img' aria-label={defaultMessage}>
            <title>{message || t('common:loading')}</title>
            <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' fill='none' />
            <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z' />
          </svg>
        ) : (
          <div className='w-full'>
            <div className={`w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden ${sizeClasses.bar}`}>
              <div
                className='bg-blue-600 h-full transition-[width] duration-300'
                style={{ width: `${Math.max(0, Math.min(100, progress ?? 30))}%` }}
              />
            </div>
          </div>
        )}

        {message && <div className='text-sm text-gray-700 dark:text-gray-300'>{message}</div>}
        {typeof progress === 'number' && (
          <div className='text-xs text-gray-500 dark:text-gray-400'>{Math.floor(Math.max(0, Math.min(100, progress)))}%</div>
        )}
      </div>
    </div>
  )
}
